import { Dialog, useTheme } from '@mui/material';

import { ATSConnectHiringRequestCandidateFeedbackTestIds } from 'data-testids/ATS';
import {
  CandidateFeedbackListModalPaneValue,
  CandidateFeedbackModalPane,
  CandidateFeedbackModalProps,
} from '../../types/feedbackDetailTypes';
import { FormContainer } from '../../styles/styles';
import { useState } from 'react';
import { CreatePane } from '../Scorecard/Create/CreatePane';
import {
  ReferenceFeedbackListPane,
  ScorecardFeedbackListPane,
} from '../FeedbackList/FeedbackListPane';
import { FormProvider, useForm } from 'react-hook-form';
import { ScorecardToSubmit, ScorecardToTransform } from '../../types/scorecardTypes';
import {
  useCreateHiringRequestScorecard,
  useEditHiringRequestScorecard,
} from '../../api/scorecardMutations';
import { CTAButtons } from '../Scorecard/Create/components/CTAButtons';

import { EditPane } from '../Scorecard/Edit/EditPane';
import {
  useQueryGetAvailableScorecardsByRoleData,
  useQueryGetScorecardsForHiringRequest,
} from '../../api/scorecardQueries';
import { ErrorSnackbar, SuccessSnackbar } from 'sharedComponents/Snackbar';
import { getAxiosErrorMessageByStatusCode } from 'hooks/errorHandling/errorHandlingHooks';
import { EditCTAButtons } from '../Scorecard/Edit/components/EditCTAButtons';
import { ReferencePane } from '../../../../../References/ReferencePane';
import {
  CandidateFeedbackListModalPane,
  CandidateFeedbackModalTypes,
  candidateFeedbackToasts,
  finalRecommendationMap,
  HiringRequestToast,
} from '../../enums';
import { HiringRequestToastType } from '../../types';
import { FinalRecommendation } from 'utils/typedEnums';
import { ReadableFinalRecommendation } from '../../types/feedbackDetailTypes';
import auth from 'utils/auth';
import { useEditReference, useQueryGetCompletedAndDraftReferences } from 'features/References/api';
import { ViewCTAButtons } from '../Scorecard/View/ViewCTAButtons';

export const CandidateFeedbackModal: React.FC<CandidateFeedbackModalProps> = ({
  currentModalOpen,
  hiringRequestId,
  open,
  onClose,
  roleTitle,
  roleId,
  candidate,
}) => {
  const [currentModalPane, setCurrentModalPane] = useState<CandidateFeedbackModalPane>({
    type:
      currentModalOpen.modalType === CandidateFeedbackModalTypes.REFERENCE
        ? CandidateFeedbackListModalPane.REFERENCE_LIST
        : CandidateFeedbackListModalPane.SCORECARD_LIST,
    id: null,
  });
  const [currentToast, setCurrentToast] = useState<{
    type: HiringRequestToastType;
    message: string;
  }>({
    type: null,
    message: null,
  });

  const { data: scorecardsAvailableForRole } = useQueryGetAvailableScorecardsByRoleData(roleId);
  const { data: completedScorecards } = useQueryGetScorecardsForHiringRequest(hiringRequestId, {
    enabled: currentModalOpen.modalType === CandidateFeedbackModalTypes.SCORECARD,
  });
  const theme = useTheme();
  const titleText =
    currentModalOpen?.modalType === CandidateFeedbackModalTypes.SCORECARD
      ? `Scorecards for ${candidate.first_name} ${candidate.last_name}`
      : `Reference Checks for ${candidate.first_name} ${candidate.last_name}`;

  const createMethods = useForm<ScorecardToSubmit>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
  });
  const { watch } = createMethods;

  const { data: completedReferences } = useQueryGetCompletedAndDraftReferences(candidate.id, {
    enabled: currentModalOpen.modalType === CandidateFeedbackModalTypes.REFERENCE,
  });
  const completedReference = completedReferences?.find(
    (reference) => reference.id === currentModalPane.id
  );

  const districtId = auth.getUser()?.profile?.district.id;
  const {
    mutate: createScorecard,
    error: createError,
    isLoading: isCreateMutationLoading,
  } = useCreateHiringRequestScorecard(districtId, {
    onSuccess: (data) => {
      setCurrentModalPane({ type: CandidateFeedbackListModalPane.SCORECARD_LIST });
      const message = data.scorecard_complete
        ? candidateFeedbackToasts.scorecardSuccess
        : candidateFeedbackToasts.scorecardDraft;
      setCurrentToast({
        type: HiringRequestToast.SUCCESS,
        message,
      });
      createMethods.reset();
    },
    onError: () => {
      setCurrentToast({ type: HiringRequestToast.ERROR, message: errorMessageFromCreating });
    },
  });
  const errorMessageFromCreating = getAxiosErrorMessageByStatusCode(createError);

  const editMethods = useForm<{ candidate_id: number; completed_scorecard_id?: number }>({
    defaultValues: { candidate_id: candidate.id },
  });
  const {
    mutate: editScorecard,
    error: editError,
    isLoading: isEditMutationLoading,
  } = useEditHiringRequestScorecard(districtId, {
    onSuccess: (data) => {
      setCurrentModalPane({ type: CandidateFeedbackListModalPane.SCORECARD_LIST });
      const message = data.scorecard_complete
        ? candidateFeedbackToasts.scorecardUpdated
        : candidateFeedbackToasts.scorecardDraft;
      setCurrentToast({
        type: HiringRequestToast.SUCCESS,
        message,
      });
      editMethods.reset();
    },
    onError: () => {
      setCurrentToast({ type: HiringRequestToast.ERROR, message: errorMessageFromEditing });
    },
  });
  const errorMessageFromEditing = getAxiosErrorMessageByStatusCode(editError);

  const {
    mutate: editReference,
    error: editReferenceError,
    isLoading: isEditReferenceMutationLoading,
  } = useEditReference(candidate.id, districtId, completedReference?.id, {
    onSuccess: () => {
      setCurrentModalPane({ type: CandidateFeedbackListModalPane.REFERENCE_LIST });
      setCurrentToast({
        type: HiringRequestToast.SUCCESS,
        message: candidateFeedbackToasts.referenceUpdated,
      });
    },
    onError: () => {
      setCurrentToast({ type: HiringRequestToast.ERROR, message: errorMessageFromEditing });
    },
  });
  const errorMessageFromEditingReference = getAxiosErrorMessageByStatusCode(editReferenceError);

  const scorecardTemplateId = watch('custom_scorecard_id');
  const selectedScorecardTemplate = scorecardsAvailableForRole?.find(
    (scorecard) => scorecard.id === scorecardTemplateId
  );
  const completedScorecard = completedScorecards?.find(
    (scorecard) => scorecard.id === currentModalPane.id
  );

  const handleSetCurrentModalPane = (pane: CandidateFeedbackModalPane) => {
    setCurrentModalPane(pane);
  };

  const updatedOnClose = (type: CandidateFeedbackListModalPaneValue) => {
    setCurrentModalPane({ type });
    onClose();
  };

  const handleCancel = (type: CandidateFeedbackListModalPaneValue) => setCurrentModalPane({ type });

  const handleSubmit = (
    formState: ScorecardToTransform,
    isComplete: boolean,
    isEdit = false,
    scorecardId?: number
  ) => {
    const formData = new FormData();

    const processedAnswers = formState.answers.map((answer) => {
      if (answer.attachment instanceof File) {
        formData.append(answer.attachment.name, answer.attachment);
        return {
          ...answer,
          attachment: answer.attachment.name,
        };
      }

      return answer;
    });

    let finalRecommendationValue: FinalRecommendation | ReadableFinalRecommendation =
      formState.final_recommendation;

    if (Object.keys(finalRecommendationMap).includes(formState.final_recommendation)) {
      finalRecommendationValue = finalRecommendationMap[formState.final_recommendation];
    }

    const payload = {
      ...formState,
      answers: processedAnswers,
      final_recommendation: finalRecommendationValue,
      ...(currentModalOpen.modalType === 'scorecard' ? { scorecard_complete: isComplete } : {}),
      ...(currentModalOpen.modalType === 'reference' ? { is_complete: isComplete } : {}),
    };

    formData.append('data', JSON.stringify(payload));

    if (isEdit) {
      if (currentModalOpen.modalType === CandidateFeedbackModalTypes.SCORECARD) {
        editScorecard({
          hiringRequestId,
          scorecardId,
          payload: formData,
        });
      } else {
        editReference({
          candidateId: candidate.id,
          referenceId: payload.id,
          payload: formData,
          isComplete,
        });
      }
    } else {
      createScorecard({
        hiringRequestId,
        payload: formData,
      });
    }
  };

  const handleSave = (formState: ScorecardToTransform, isComplete: boolean) => {
    if (completedScorecard) {
      handleSubmit(formState, isComplete, true, completedScorecard.id);
    } else if (completedReference) {
      handleSubmit(formState, isComplete, true, completedReference.id);
    } else {
      handleSubmit(formState, isComplete, false, null);
    }
  };

  const handleEdit = (formState: ScorecardToTransform) => {
    const completedScorecardId = formState.completed_scorecard_id;
    const filteredFormState = Object.fromEntries(
      Object.entries(formState).filter(
        ([key]) =>
          key !== 'submitted_by' &&
          key !== 'completed_scorecard_id' &&
          (currentModalOpen.modalType === CandidateFeedbackModalTypes.SCORECARD
            ? key !== 'candidate_id'
            : true)
      )
    );

    handleSubmit(
      {
        ...filteredFormState,
        custom_scorecard_id: formState.custom_scorecard_id,
        answers: formState.answers,
      },
      true,
      true,
      currentModalOpen.modalType === CandidateFeedbackModalTypes.SCORECARD
        ? completedScorecardId || formState.id
        : formState.id
    );
  };

  const handleEditClick = (id: number) => {
    const type =
      currentModalOpen.modalType === CandidateFeedbackModalTypes.SCORECARD
        ? CandidateFeedbackListModalPane.SCORECARD_EDIT
        : CandidateFeedbackListModalPane.REFERENCE_EDIT;

    return handleSetCurrentModalPane({
      type,
      id,
    });
  };

  const handleViewClick = (id: number) => {
    const type =
      currentModalPane.type === CandidateFeedbackListModalPane.SCORECARD_LIST
        ? CandidateFeedbackListModalPane.SCORECARD_VIEW
        : CandidateFeedbackListModalPane.REFERENCE_VIEW;

    return handleSetCurrentModalPane({
      type,
      id,
    });
  };

  return (
    <Dialog
      data-testid={ATSConnectHiringRequestCandidateFeedbackTestIds.MODAL}
      maxWidth="md"
      fullWidth
      onClose={updatedOnClose}
      open={open}
      sx={{
        color: theme.palette.text.primary,
      }}
      PaperProps={{
        style: {
          borderRadius: theme.spacing(2),
        },
      }}
    >
      <FormContainer>
        {currentModalPane.type === CandidateFeedbackListModalPane.SCORECARD_LIST && (
          <FormProvider {...editMethods}>
            <ScorecardFeedbackListPane
              onClose={() => updatedOnClose(CandidateFeedbackListModalPane.SCORECARD_LIST)}
              roleTitle={roleTitle}
              titleText={titleText}
              onModalPaneChange={handleSetCurrentModalPane}
              modalType={currentModalOpen.modalType}
              hiringRequestId={hiringRequestId}
              onEditClick={handleEditClick}
              onViewClick={handleViewClick}
            />
          </FormProvider>
        )}
        {currentModalPane.type === CandidateFeedbackListModalPane.REFERENCE_LIST && (
          <FormProvider {...editMethods}>
            <ReferenceFeedbackListPane
              onClose={() => updatedOnClose(CandidateFeedbackListModalPane.REFERENCE_LIST)}
              roleTitle={roleTitle}
              titleText={titleText}
              onModalPaneChange={handleSetCurrentModalPane}
              modalType={currentModalOpen.modalType}
              onEditClick={handleEditClick}
              onViewClick={handleViewClick}
              candidateId={candidate.id}
            />
          </FormProvider>
        )}
        {currentModalPane.type === CandidateFeedbackListModalPane.SCORECARD_CREATE && (
          <FormProvider {...createMethods}>
            <CreatePane
              candidateName={`${candidate.first_name} ${candidate.last_name}`}
              modalType={currentModalOpen?.modalType}
              roleId={roleId}
            />
            <CTAButtons
              hasSelectedScorecard={Boolean(selectedScorecardTemplate)}
              onCancel={() => {
                createMethods.reset();
                handleCancel(CandidateFeedbackListModalPane.SCORECARD_LIST);
              }}
              onSaveAsDraft={(formState) => handleSave(formState, false)}
              onSaveAsCompleted={(formState) => handleSave(formState, true)}
              isLoading={isCreateMutationLoading}
            />
          </FormProvider>
        )}
        {currentModalPane.type === CandidateFeedbackListModalPane.REFERENCE_CREATE && (
          <ReferencePane
            onCancel={() => handleCancel(CandidateFeedbackListModalPane.REFERENCE_LIST)}
            hiringRequestId={hiringRequestId}
            setFeedbackModalToast={setCurrentToast}
            candidate={candidate}
            roleTitle={roleTitle}
          />
        )}
        {currentModalPane.type === CandidateFeedbackListModalPane.SCORECARD_EDIT && (
          <FormProvider {...editMethods}>
            <EditPane
              candidateName={`${candidate.first_name} ${candidate.last_name}`}
              created={completedScorecard.created}
              modalType={currentModalOpen?.modalType}
              reviewer={completedScorecard.submitted_by.name}
              templateName={completedScorecard.template.title}
              isReadOnly={false}
            />
            <EditCTAButtons
              isLoading={isEditMutationLoading}
              onSave={(formState) => handleEdit(formState)}
              onCancel={() => handleCancel(CandidateFeedbackListModalPane.SCORECARD_LIST)}
              isScorecard={true}
              isComplete={completedScorecard.scorecard_complete}
              onSaveAsDraft={(formState) => {
                handleSave(formState, false);
              }}
            />
          </FormProvider>
        )}
        {currentModalPane.type === CandidateFeedbackListModalPane.REFERENCE_EDIT && (
          <FormProvider {...editMethods}>
            <EditPane
              candidateName={`${candidate.first_name} ${candidate.last_name}`}
              created={completedReference.created}
              modalType={currentModalOpen?.modalType}
              reviewer={completedReference.submitted_by_name}
              templateName={completedReference.template_name}
            />
            <EditCTAButtons
              isLoading={isEditReferenceMutationLoading}
              onSave={(formState) => handleEdit(formState)}
              onCancel={() => handleCancel(CandidateFeedbackListModalPane.REFERENCE_LIST)}
              isScorecard={false}
              isComplete={completedReference.is_complete}
              onSaveAsDraft={(formState) => {
                handleSave(formState, false);
              }}
            />
          </FormProvider>
        )}
        {currentModalPane.type === CandidateFeedbackListModalPane.REFERENCE_VIEW && (
          <FormProvider {...editMethods}>
            <EditPane
              candidateName={`${candidate.first_name} ${candidate.last_name}`}
              created={completedReference.created}
              modalType={currentModalOpen.modalType}
              reviewer={completedReference.submitted_by_name}
              templateName={completedReference.template_name}
              isReadOnly={true}
            />
            <ViewCTAButtons
              onEditClick={() => handleEditClick(currentModalPane.id)}
              onCancel={() => handleCancel(CandidateFeedbackListModalPane.REFERENCE_LIST)}
              authorEmail={completedReference.submitted_by}
            />
          </FormProvider>
        )}
        {currentModalPane.type === CandidateFeedbackListModalPane.SCORECARD_VIEW && (
          <FormProvider {...editMethods}>
            <EditPane
              candidateName={`${candidate.first_name} ${candidate.last_name}`}
              created={completedScorecard.created}
              modalType={currentModalOpen.modalType}
              reviewer={completedScorecard.submitted_by.name}
              templateName={completedScorecard.template.title}
              isReadOnly={true}
            />
            <ViewCTAButtons
              onEditClick={() => handleEditClick(currentModalPane.id)}
              onCancel={() => handleCancel(CandidateFeedbackListModalPane.SCORECARD_LIST)}
              authorEmail={completedScorecard.submitted_by.email}
            />
          </FormProvider>
        )}
      </FormContainer>

      <ErrorSnackbar
        dataTestId={ATSConnectHiringRequestCandidateFeedbackTestIds.ERROR_TOAST}
        open={currentToast?.type === HiringRequestToast.ERROR}
        message={
          errorMessageFromCreating || errorMessageFromEditing || errorMessageFromEditingReference
        }
        onClose={() => setCurrentToast({ type: null, message: null })}
      />
      <SuccessSnackbar
        dataTestId={ATSConnectHiringRequestCandidateFeedbackTestIds.SUCCESS_TOAST}
        open={currentToast?.type === HiringRequestToast.SUCCESS}
        message={currentToast?.message}
        onClose={() => setCurrentToast(null)}
      />
    </Dialog>
  );
};
